import React, { useCallback, useMemo, useState } from 'react';
import { isEmpty } from 'ramda';
import { Box, Chip, IconButton } from '@material-ui/core';
import ArrowBackIosSharpIcon from '@material-ui/icons/ArrowBackIosSharp';
import { useIntl } from 'react-intl';

import { useConnect } from './hooks';
import { usePageView } from '../../../hooks/analytics';
import piwik from '../../../services/analytics/analytics';
import { POI_LOCATION, eventTags } from '../../../services/analytics/tags';

import Button from '../../../components/Button/Button';
import Footer from '../../../components/Footer/Footer';
import Page from '../../../components/Page/Page';
import Title from '../../../components/Title/Title';
import Typography from '../../../components/Typography/Typography';
import MultiPicker from '../../../components/Inputs/MultiPicker/MultiPicker';
import theme from '../../../theme';

import generalMessages, { getPointOfInterestTypeLabel } from '../../../messages';
import messages from './messages';

const NOT_LISTED = 'NOT_LISTED';

const POILocation = () => {
  usePageView(POI_LOCATION);

  const intl = useIntl();
  const { back, next, pointOfInterests, poiDigitalExit, highwayPOINotListed, country } = useConnect();
  const [selectedPOI, setSelectedPOI] = useState({});

  const poiOptions = useMemo(
    () => [
      ...(pointOfInterests || []).map(p => ({
        name: getPointOfInterestTypeLabel(p.type),
        value: { ...p, country },
        subContent: (
          <>
            <Box
              style={{
                fontWeight: 'normal',
              }}
            >
              {p.title}
            </Box>
            <Chip
              label={`${intl.formatMessage(messages.milestone_label)}: ${p.milestone}`}
              style={{
                backgroundColor: '#E6E9F4',
                borderRadius: '4px',
                color: theme.palette.primary.main,
                marginBottom: '5px',
              }}
            />
          </>
        ),
      })),
      { name: messages.locationNotListed, value: NOT_LISTED },
    ],
    [country, intl, pointOfInterests]
  );

  const handleBack = useCallback(() => {
    piwik.trackEvent(eventTags.back_to_previous_page);
    back();
  }, [back]);

  const confirm = useCallback(() => {
    if (isEmpty(selectedPOI)) {
      return;
    }

    if (selectedPOI === NOT_LISTED) {
      piwik.trackEvent(eventTags.poi_location_not_listed);
      highwayPOINotListed();
      return;
    }

    if (selectedPOI.shouldExit) {
      piwik.trackEvent(eventTags.poi_location_should_exit);
      poiDigitalExit();
      return;
    }

    piwik.trackEvent(eventTags.poi_location_select);
    next(selectedPOI);
  }, [selectedPOI, next, highwayPOINotListed, poiDigitalExit]);

  return (
    <Page name="poi-location" direction="column">
      <IconButton name="back" color="primary" style={{ position: 'absolute' }} onClick={handleBack}>
        <ArrowBackIosSharpIcon color="primary" />
      </IconButton>
      <Box style={{ flex: 1 }}>
        <Title content={messages.title} src="POILocation" />
        <Typography content={messages.subtitle} />
        <Box style={{ marginTop: 20 }}>
          <MultiPicker content={poiOptions} spacing={2} onChange={setSelectedPOI} selected={selectedPOI} />
        </Box>
      </Box>
      <Footer direction="column">
        <Button
          name="confirm-poi-location"
          id="confirm-poi-location"
          testId="confirm-poi-location"
          content={generalMessages.confirm}
          disabled={isEmpty(selectedPOI)}
          onClick={confirm}
        />
      </Footer>
    </Page>
  );
};

export default POILocation;
